import React, { useState } from "react";
import * as XLSX from "xlsx";
import { ExpectedPayment, ReceivedPayment, ReconRow, parseRemittanceSheet, parseRemittanceText, reconcile, summarize } from "../data/reconciliationEngine";

const RESIDENTS_KEY = "residents_records";
const INVOICES_KEY = "invoices_records";
const RESULTS_KEY = "recon_results";

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

const DEMO_RESIDENTS = [
  { name: "Margaret Hughes", funder: "Council", rate: "3400", admissionDate: "2025-03-12", exitDate: "", familyEmail: "family.hughes@example.com", address: "14 High Street, Pontefract" },
  { name: "John Barker", funder: "Private", rate: "3200", admissionDate: "2024-11-02", exitDate: "", familyEmail: "j.barker@example.com", address: "2 Mill Lane, Wakefield" },
  { name: "Alice Whitfield", funder: "Mixed", rate: "3600", admissionDate: "2025-06-20", exitDate: "", familyEmail: "awhitfield@example.com", address: "8 Church Road, Pontefract" },
  { name: "Frank Saunders", funder: "Council", rate: "3500", admissionDate: "2025-05-11", exitDate: "", familyEmail: "frank.s@example.com", address: "3 Queen Street, Castleford" },
  { name: "Peter Osborne", funder: "Council", rate: "3300", admissionDate: "2025-01-15", exitDate: "2026-07-30", familyEmail: "p.osborne@example.com", address: "5 King Street, Pontefract" },
  { name: "Doris Kemp", funder: "Private", rate: "0", admissionDate: "2025-09-01", exitDate: "", familyEmail: "dkemp@example.com", address: "9 Bridge Street, Wakefield" },
];

const SAMPLE_REMITTANCE = `Margaret Hughes 3400
John Barker 3200
Alice Whitfield 3000`;

export default function Reconciliation({ user }: { user?: any }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [residents, setResidents] = useState<any[]>(() => loadJSON(RESIDENTS_KEY, []));
  const [received, setReceived] = useState<ReceivedPayment[]>([]);
  const [remitText, setRemitText] = useState("");
  const [rows, setRows] = useState<ReconRow[]>([]);
  const [results, setResults] = useState<any[]>(() => loadJSON(RESULTS_KEY, []));
  const [message, setMessage] = useState("");

  const periodStart = `${period}-01`;

  const activeResidents = residents.filter(r => !r.exitDate || r.exitDate >= periodStart);
  const leavers = residents.filter(r => r.exitDate && r.exitDate < periodStart);
  const noRate = activeResidents.filter(r => !(parseFloat(r.rate) > 0));

  const seedDemo = () => {
    const existing = loadJSON<any[]>(RESIDENTS_KEY, []);
    const merged = [...existing];

    DEMO_RESIDENTS.forEach(demo => {
      if (!merged.some(r => r.name === demo.name)) {
        merged.push({ id: `res-demo-${demo.name.replace(/\s/g, "-").toLowerCase()}`, dob: "", nhsNumber: "", gpName: "", gpPhone: "", familyName: "", familyPhone: "", dietary: "", allergies: "", mobility: "", notes: "Demo record", ...demo });
      }
    });

    localStorage.setItem(RESIDENTS_KEY, JSON.stringify(merged));
    setResidents(merged);
    setRemitText(SAMPLE_REMITTANCE);
    setMessage("Demo data loaded: 6 residents (1 leaver excluded, 1 with no rate flagged). Sample remittance ready. Now click Generate invoices, then Run reconciliation.");
  };

  const generateInvoices = () => {
    const invoices = loadJSON<any[]>(INVOICES_KEY, []);
    let created = 0;

    activeResidents.forEach(r => {
      const rate = parseFloat(r.rate) || 0;
      if (rate <= 0) return;
      if (invoices.some(i => i.residentName === r.name && i.period === period)) return;

      invoices.unshift({
        id: `inv-${Date.now()}-${created}`,
        residentId: r.id,
        residentName: r.name,
        period,
        baseCharge: rate,
        haircut: 0, food: 0, drinks: 0, takeaway: 0, other: 0, otherNote: "",
        total: rate,
        status: "Saved",
        createdAt: new Date().toISOString(),
        sentMethod: "", sentAt: "",
      });
      created += 1;
    });

    localStorage.setItem(INVOICES_KEY, JSON.stringify(invoices));
    setMessage(`Generated ${created} invoice(s) for ${period} from agreed rates. Leavers (${leavers.map(l => l.name).join(", ") || "none"}) excluded.`);
  };

  const handleRemitFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        setReceived(parseRemittanceSheet(XLSX.utils.sheet_to_json<any>(sheet)));
        setMessage(`Loaded remittance ${file.name}.`);
      } catch {
        setMessage("Could not read that file. Use .xlsx/.xls/.csv or paste text.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const buildExpected = (): ExpectedPayment[] => {
    const invoices = loadJSON<any[]>(INVOICES_KEY, []).filter(i => i.period === period);
    const expected: ExpectedPayment[] = invoices.map(i => ({ residentName: i.residentName, funder: residents.find(r => r.name === i.residentName)?.funder || "Unknown", expected: i.total, source: "Invoice", period }));
    const invoiced = new Set(invoices.map(i => i.residentName));

    activeResidents.forEach(r => {
      const rate = parseFloat(r.rate) || 0;
      if (rate > 0 && !invoiced.has(r.name)) expected.push({ residentName: r.name, funder: r.funder || "Unknown", expected: rate, source: "Rate - no invoice", period });
    });

    return expected;
  };

  const runReconcile = () => {
    const expected = buildExpected();
    const got = received.length ? received : parseRemittanceText(remitText);

    if (expected.length === 0) { setMessage("No expected payments. Load demo data or add residents with rates."); return; }
    if (got.length === 0) { setMessage("Load or paste the council / private remittance first."); return; }

    setReceived(got);
    setRows(reconcile(expected, got));
    setMessage(`Reconciled ${expected.length} expected vs ${got.length} received for ${period}.`);
  };

  const confirmRow = (name: string) => setRows(c => c.map(r => (r.residentName === name ? { ...r, confirmed: true, confirmedAt: new Date().toISOString() } : r)));
  const confirmAll = () => setRows(c => c.map(r => (r.received > 0 ? { ...r, confirmed: true, confirmedAt: new Date().toISOString() } : r)));

  const printInvoice = (name: string) => {
    const res = residents.find(r => r.name === name);
    const row = rows.find(r => r.residentName === name);
    if (!row) return;

    const html = `<!DOCTYPE html><html><head><title>Invoice</title><style>body{font-family:Arial;margin:40px;color:#1f2937}.head{display:flex;justify-content:space-between;border-bottom:3px solid #123B36;padding-bottom:12px}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{padding:8px;border-bottom:1px solid #e5e7eb;font-size:14px}.r{text-align:right}</style></head><body>
<div class="head"><div><img src="/logo512.png" width="60" height="60"/><br/><strong>Roop Cottage Residential Care Home</strong><br/>Wakefield Road, Fitzwilliam, Pontefract, WF9 5AN</div><div class="r"><h2>INVOICE</h2><div>Period: ${period}</div></div></div>
<h3>Billed to: ${name}${res && res.familyName ? ` (c/o ${res.familyName})` : ""}</h3>${res && res.address ? `<p>${res.address}</p>` : ""}
<table><tr><th>Description</th><th class="r">Amount</th></tr><tr><td>Base care fee (${res ? res.funder : ""})</td><td class="r">£${row.expected.toFixed(2)}</td></tr><tr><td class="r"><strong>TOTAL</strong></td><td class="r"><strong>£${row.expected.toFixed(2)}</strong></td></tr></table>
<p style="margin-top:24px;font-size:12px">Please pay within 14 days.</p></body></html>`;

    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  const emailInvoice = (name: string) => {
    const res = residents.find(r => r.name === name);
    const row = rows.find(r => r.residentName === name);
    if (!row) return;

    const to = res && res.familyEmail ? res.familyEmail : "";
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(`Invoice - ${name} - ${period}`)}&body=${encodeURIComponent(`Invoice for ${name}, period ${period}. Total due: £${row.expected.toFixed(2)}.\n\nRoop Cottage Residential Care Home`)}`;
  };

  const saveReport = () => {
    if (rows.length === 0) { setMessage("Run a reconciliation first."); return; }
    const next = [{ id: `rec-${Date.now()}`, period, createdAt: new Date().toISOString(), rows }, ...results];
    setResults(next);
    localStorage.setItem(RESULTS_KEY, JSON.stringify(next));
    setMessage(`Monthly report saved for ${period}.`);
  };

  const exportExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(rows.map(r => ({ Resident: r.residentName, Funder: r.funder, Expected: r.expected, Received: r.received, Difference: r.difference, Status: r.status, BankConfirmed: r.confirmed ? "Yes" : "No" })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Reconciliation");
    XLSX.writeFile(workbook, `reconciliation-${period}.xlsx`);
  };

  const exportPdf = () => {
    const summary = summarize(rows);
    const body = rows.map(r => `<tr><td>${r.residentName}</td><td>${r.funder}</td><td>£${r.expected.toFixed(2)}</td><td>£${r.received.toFixed(2)}</td><td>${r.status}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Report</title><style>body{font-family:Arial;margin:40px}table{width:100%;border-collapse:collapse}td,th{padding:6px;border-bottom:1px solid #ddd;font-size:13px}</style></head><body><h2>Reconciliation report ${period}</h2><p>Expected £${summary.totalExpected.toFixed(2)} | Received £${summary.totalReceived.toFixed(2)} | Outstanding £${summary.outstanding.toFixed(2)}</p><table><tr><th>Resident</th><th>Funder</th><th>Expected</th><th>Received</th><th>Status</th></tr>${body}</table></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  const summary = summarize(rows);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Intelligent Reconciliation</h2>
        <p className="text-sm text-slate-600">Collects residents from admissions, excludes leavers, generates invoices from agreed rates, matches council and private remittances, confirms bank receipt and exports Excel / PDF reports.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-slate-900">Step 1 - Period and data</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Month</label>
            <input type="month" value={period} onChange={event => setPeriod(event.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <button onClick={seedDemo} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Load demo data</button>
          <button onClick={generateInvoices} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Generate invoices</button>
        </div>
        <div className="mt-2 text-xs text-slate-600">Active this month: {activeResidents.length} | Leavers excluded: {leavers.map(l => l.name).join(", ") || "none"} | Missing a rate: {noRate.map(n => n.name).join(", ") || "none"}</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-slate-900">Step 2 - Remittance (the only thing you upload)</h3>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleRemitFile} className="mb-2 w-full text-sm" />
        <textarea value={remitText} onChange={event => setRemitText(event.target.value)} rows={3} placeholder="Or paste remittance lines: name and amount" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-slate-900">Step 3 - Reconcile, send invoices, confirm bank</h3>
        <div className="mb-3 flex flex-wrap gap-2">
          <button onClick={runReconcile} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Run reconciliation</button>
          <button onClick={confirmAll} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Confirm all received (bank)</button>
        </div>

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr><th className="px-2 py-2">Resident</th><th className="px-2 py-2">Funder</th><th className="px-2 py-2">Expected</th><th className="px-2 py-2">Received</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Invoice</th><th className="px-2 py-2">Bank</th></tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.residentName} className="border-b border-slate-100">
                    <td className="px-2 py-2 font-medium">{row.residentName}</td>
                    <td className="px-2 py-2">{row.funder}</td>
                    <td className="px-2 py-2">£{row.expected.toFixed(2)}</td>
                    <td className="px-2 py-2">£{row.received.toFixed(2)}</td>
                    <td className="px-2 py-2"><span className={row.status === "PAID IN FULL" ? "rounded-full bg-green-100 px-2 py-0.5 text-green-700" : row.status === "NOT PAID" ? "rounded-full bg-red-100 px-2 py-0.5 text-red-700" : "rounded-full bg-amber-100 px-2 py-0.5 text-amber-700"}>{row.status}</span></td>
                    <td className="px-2 py-2"><button onClick={() => printInvoice(row.residentName)} className="mr-1 rounded border border-slate-300 px-2 py-1">Print</button><button onClick={() => emailInvoice(row.residentName)} className="rounded border border-slate-300 px-2 py-1">Email</button></td>
                    <td className="px-2 py-2">{row.confirmed ? <span className="text-green-700">Received</span> : <button onClick={() => confirmRow(row.residentName)} className="rounded border border-slate-300 px-2 py-1">Confirm</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-slate-900">Step 4 - Report</h3>
        <div className="mb-3 flex flex-wrap gap-2">
          <button onClick={saveReport} className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white">Save report</button>
          <button onClick={exportExcel} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Export Excel</button>
          <button onClick={exportPdf} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Export PDF</button>
        </div>

        {rows.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-lg font-semibold">£{summary.totalExpected.toFixed(0)}</div><div className="text-xs text-slate-500">Expected</div></div>
            <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-lg font-semibold text-green-700">£{summary.totalReceived.toFixed(0)}</div><div className="text-xs text-slate-500">Received</div></div>
            <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-lg font-semibold text-red-700">£{summary.outstanding.toFixed(0)}</div><div className="text-xs text-slate-500">Outstanding</div></div>
            <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-lg font-semibold">{summary.notPaidCount}</div><div className="text-xs text-slate-500">Unpaid / short</div></div>
          </div>
        ) : null}

        <div className="mt-3 space-y-2">
          {Object.keys(summary.byFunder).map(funder => (
            <div key={funder} className="rounded-lg border border-slate-100 p-2 text-sm text-slate-700">{funder}: expected £{summary.byFunder[funder].expected.toFixed(0)}, received £{summary.byFunder[funder].received.toFixed(0)}, gap £{(summary.byFunder[funder].expected - summary.byFunder[funder].received).toFixed(0)}</div>
          ))}
        </div>

        <div className="mt-3 text-xs text-slate-500">Saved reports: {results.length}</div>
      </div>
    </div>
  );
}